import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function Scanners()  {
  const TABS = [
    "Monthly Cycles",
    "Price + OI + PCR",
    "Price + Delivery + ATS",
    "Correlation + Arbitrage",
  ];

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [monthlyCycleData, setMonthlyCycleData] = useState({}); // API returns object: { "January-2026": [...] }
  const [avg1MSortDir, setAvg1MSortDir] = useState("desc"); // sorting option for Avg_1M
  const [user, setUser] = useState(undefined); // undefined = loading
  const [technicalsData, setTechnicalsData] = useState({});
  const [token, setToken] = useState(null);
  const auth = getAuth();

  const [sortConfig, setSortConfig] = React.useState({
    key: "chg",
    direction: "desc",
  });


  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (user === undefined) return;   // auth loading
    if (!user) {
      setToken(null);
      return;
    }

    user.getIdToken()
      .then(setToken)
      .catch(() => setToken(null));
  }, [user]);


  useEffect(() => {
    if (!token) return;

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/scanners/monthly_cycles`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setMonthlyCycleData(res.data))
      .catch((err) => console.error("Failed to fetch monthly cycles", err));
  }, [token]);

  useEffect(() => {
    if (!token) return;

    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/scanners/technicals`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setTechnicalsData(res.data))
      .catch((err) => console.error("Failed to fetch Technical data", err))
      .finally(() => setLoading(false));
  }, [token]);


  const updateFreq = {
    "Monthly Cycles": (
      <>
        <p className="mb-2 text-blue-600">
          This scanner update every month on 1st.
        </p>
      </>
    ),

    "Price + OI + PCR": (
      <>
        <p className="mb-2 text-blue-600">
          This scanner updates daily 10 PM.
        </p>
      </>
    ),

    "Price + Delivery + ATS": (
      <>
        <p className="mb-2 text-blue-600">
          This scanner updates daily 10 PM.
        </p>
      </>
    ),

    "Correlation + Arbitrage": (
      <>
        <p className="mb-2 text-blue-600">
          This scanner updates daily 10 PM.
        </p>
      </>
    ),  
  }

  const tips = {
    "Monthly Cycles": (
      <>
        <p className="mb-2">
          This scanner helps you identify <b>repeatable monthly behaviour</b> in stocks or sectors.Certain stocks tend to perform well in specific months due to:
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li>Quarterly result cycles</li>
          <li>Budget, policy, or macro seasonality</li>
          <li>Institutional portfolio rebalancing</li>
        </ul>
        <p>
          Use this to align your swing trades with <b>historical probability</b>, not prediction.
        </p>
      </>
    ),

    "Price + OI + PCR": (
      <>
        <p className="mb-2">
          This scanner combines <b>price action</b> with <b>derivative participation</b>.
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li><b>Price</b> tells you direction</li>
          <li><b>Open Interest</b> = Total Open Futures contracts in percentage terms. This tells you participation. Use this along with Price Change. Also looks for divergence i.e price going up but OI going down.</li>
          <li><b>PCR</b> = Total PE Open contracts/Total CE Open Contracts. This ratio hints where positioning is skewed towards Call writers or Put writers. If There are more call writers it can act as resistance, if there are more put writers it can act as
          support.</li>
        </ul>
        <p className="mb-2">
          The goal is <b>not to do options trading</b>, but understanding market structure:
        </p>
        <ul className="list-disc ml-5">
          <li>Fresh buildup vs short covering</li>
          <li>Supported by participation or getting exhausted</li>
        </ul>
      </>
    ),

    "Price + Delivery + ATS": (
      <>
        <p className="mb-2">
          This scanner focuses on <b>cash-market conviction</b>.
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li><b>Price</b> tells you direction</li>
          <li><b>Delivery %</b> = Total Delivery Volume in percentage terms.This shows how much volume turned into ownership, this removes bulk deals.</li>
          <li><b>ATS</b> (Average Trade Size) = Total Volume/Total Number of trades.This indicates institutional vs retail activity.
          If ATS is high it can be insider or institutional activity. If ATS is low it can be retail activity.
          </li>
        </ul>
        <p className="mb-2">
          A price move with <b>high delivery and rising ATS</b> at lower level usually signals:
        </p>
        <ul className="list-disc ml-5">
          <li>Stronger hands entering</li>
        </ul>
      </>
    ),

    "Correlation + Arbitrage": (
      <>
        <p className="mb-2">
          This scanner looks for <b>relative mispricing</b> instead of absolute direction.
        </p>
        <ul className="list-disc ml-5 mb-2">
          <li>Stock vs Sector divergence</li>
          <li>Peer-to-peer relative weakness or strength</li>
          <li>Equity vs commodity or macro link breaks</li>
          <li>Indian Stocks vs Global Peers</li>
          <li>Revaluations due to new IPO</li>
        </ul>
        <p>
          Useful when markets are range-bound and <b>alpha comes from relationships</b>,
          not trends.
        </p>
      </>
    ),
  };

  const monthKeys = useMemo(() => {
    // Try to sort by YYYY-MM then fallback to alpha
    const keys = Object.keys(monthlyCycleData || {});
    const monthMap = {
      january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
      july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
    };

    const parseKey = (k) => {
      // "January-2026"
      const [mNameRaw, yRaw] = String(k).split("-");
      const y = Number(yRaw);
      const m = monthMap[String(mNameRaw || "").trim().toLowerCase()] || 0;
      return { y: isFinite(y) ? y : 0, m };
    };

    return keys.sort((a, b) => {
      const A = parseKey(a);
      const B = parseKey(b);
      if (A.y !== B.y) return A.y - B.y;   // oldest year first
      if (A.m !== B.m) return A.m - B.m;   // oldest month first
      return String(a).localeCompare(String(b));
    });
  }, [monthlyCycleData]);

  const technicalsMap = useMemo(() => {
    const map = {};
    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    arr.forEach(t => {
      if (t?.symbol) {
        map[t.symbol] = t;
      }
    });

    return map;
  }, [technicalsData]);

  const monthlyCycleDataWithPrice = useMemo(() => {
    const out = {};

    Object.entries(monthlyCycleData || {}).forEach(([monthKey, rows]) => {
      out[monthKey] = (rows || []).map(r => {
        const t = technicalsMap[r.Symbol];

        return {
          ...r,
          currentPrice: t?.currentPrice ?? null,
          previousClose: t?.previousClose ?? null, // optional (future use)
        };
      });
    });

    return out;
  }, [monthlyCycleData, technicalsMap]);

  const ColorChange = ({ value, suffix = "%", digits = 2 }) => {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return <span className="text-gray-500">—</span>;

    const color =
      n > 0 ? "text-green-600" : n < 0 ? "text-red-600" : "text-gray-600";

    return (
      <span className={color}>
        {`${n > 0 ? "+" : ""}${n.toFixed(digits)}${suffix}`}
      </span>
    );
  };

  const SortableTh = ({ label, sortKey, subLabel }) => {
    const active = sortConfig.key === sortKey;
    const dir = sortConfig.direction;

    return (
      <th
        onClick={() =>
          setSortConfig((prev) => ({
            key: sortKey,
            direction:
              prev.key === sortKey && prev.direction === "desc" ? "asc" : "desc",
          }))
        }
        className={`cursor-pointer select-none text-right px-3 py-2 ${
          active ? "text-gray-900" : ""
        }`}
      >
        <div className="flex flex-col items-end">
          <span className="flex items-center gap-1 text-blue-600">
            {label}
            {active && (dir === "asc" ? "↑" : "↓")}
          </span>
          {subLabel && <span className="text-xs text-gray-400">{subLabel}</span>}
        </div>
      </th>
    );
  };

  const renderMonthlyCycles = () => {
    if (loading) {
      return <div className="mt-3 text-sm text-gray-500">Loading…</div>;
    }

    if (!monthKeys.length) {
      return <div className="mt-3 text-sm text-red-500">No data available at the moment</div>;
    }

    return (
      <div className="mt-4 space-y-2">
        {/* Global sorting option for Avg_1M */}
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-500">Sort all tables by Avg_1M:</div>
          <button
            className="text-xs px-3 py-1 rounded border bg-white hover:bg-gray-50"
            onClick={() => setAvg1MSortDir((d) => (d === "desc" ? "asc" : "desc"))}
            title="Toggle Avg_1M sorting direction"
          >
            {avg1MSortDir === "desc" ? "Descending ↓" : "Ascending ↑"}
          </button>
        </div>

        {monthKeys.map((monthKey) => {
          const rowsRaw = monthlyCycleDataWithPrice?.[monthKey] || [];

          // sort rows by Avg_1M desc/asc (numeric)
          const rows = [...rowsRaw].sort((a, b) => {
            const av = Number(a?.Avg_1M ?? a?.avg_1m ?? 0);
            const bv = Number(b?.Avg_1M ?? b?.avg_1m ?? 0);
            return avg1MSortDir === "desc" ? (bv - av) : (av - bv);
          });

          // columns from the first row
          const cols = rows.length ? Object.keys(rows[0]) : [];

          const publishedDate =
            rows.length && rows[0]?.recordDate
              ? rows[0].recordDate
              : null;

          return (
            <div key={monthKey} className="border rounded bg-white">
              <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
                {/* Left side */}
                <div className="flex items-baseline gap-2">
                  <div className="font-semibold text-gray-800">{monthKey}</div>
                  {publishedDate && (
                    <div className="text-xs text-gray-500">
                      Published: {publishedDate}
                    </div>
                  )}
                </div>

                {/* Right side */}
                <div className="text-xs text-gray-500">
                  {rows.length} rows
                </div>
              </div>

              {rows.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">No rows</div>
              ) : (
                <div className="overflow-auto">
                  <table className="min-w-full text-xs">

                    <thead>
                      <tr>
                        <th className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 left-0 bg-gray-100 z-30">
                          Name
                        </th>

                        {/* Industry header */}
                        <th className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20">
                          Industry
                        </th>

                        {/* Price header */}
                        <th className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20">
                          Published Price
                        </th>

                        {/* Current price */}
                        <th className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20">
                          Current Price
                        </th>

                        {/* Current price */}
                        <th className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20">
                          Chg
                        </th>

                        {cols
                        .filter(c => c !== "Symbol" && c !== "Name" && c !== "recordDate" && c !== "Industry" && c !== "recordPrice" &&
                                    c !== "currentPrice" && c !== "previousClose"
                        )
                        .map((c) => (
                          <th key={c} className="border px-2 py-1 text-left whitespace-nowrap sticky top-0 bg-gray-100 z-20">
                            {c}
                            {String(c).toLowerCase() === "avg_1m" && (
                              <span className="ml-1 text-gray-400">
                                {avg1MSortDir === "desc" ? "↓" : "↑"}
                              </span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={idx} className="group hover:bg-blue-50">
                          {/* Company Name (sticky, clickable) */}
                          <td className="p-2 sticky left-0 bg-white z-10 font-medium whitespace-nowrap group-hover:bg-blue-50">
                            <a
                              href={`/symbol/${r.Symbol}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {r.Name || "—"}
                            </a>
                          </td>

                          {/* Industry */}
                          <td className="border px-2 py-1 whitespace-nowrap">
                            {r.Industry || "—"}
                          </td>

                          {/* recordPrice */}
                          <td className="border px-2 py-1 whitespace-nowrap">
                            {r.recordPrice != null && String(r.recordPrice).trim() !== ""
                              ? Number(r.recordPrice).toLocaleString("en-IN", { maximumFractionDigits: 2 })
                              : "—"}
                          </td>

                          {/* currentPrice */}
                          <td
                            className={`border px-2 py-1 whitespace-nowrap ${
                              r.currentPrice != null && r.recordPrice != null
                                ? Number(r.currentPrice) > Number(r.recordPrice)
                                  ? "text-green-600"
                                  : "text-red-600"
                                : "text-gray-600"
                            }`}
                          >
                            {r.currentPrice != null
                              ? Number(r.currentPrice).toLocaleString("en-IN", { maximumFractionDigits: 2 })
                              : "—"}
                          </td>

                          {/* percentage change */}
                          <td
                            className={`border px-2 py-1 whitespace-nowrap ${
                              r.currentPrice != null && r.recordPrice != null && Number(r.recordPrice) !== 0
                                ? ((Number(r.currentPrice) - Number(r.recordPrice)) / Number(r.recordPrice)) * 100 > 0
                                  ? "text-green-600"
                                  : "text-red-600"
                                : "text-gray-600"
                            }`}
                          >
                            {r.currentPrice != null && r.recordPrice != null && Number(r.recordPrice) !== 0
                              ? (() => {
                                  const pct =
                                    ((Number(r.currentPrice) - Number(r.recordPrice)) / Number(r.recordPrice)) * 100;
                                  return `${pct > 0 ? "+" : ""}${pct.toFixed(2)}%`;
                                })()
                              : "—"}
                          </td>


                          {/* Remaining columns */}
                          {cols
                            .filter(c => c !== "Symbol" && c !== "Name" && c !== "recordDate" && c !== "Industry" && c !== "recordPrice" &&
                                        c !== "currentPrice" && c !== "previousClose"
                            )
                            .map((c) => {
                              const raw = r?.[c];

                              let display = "—";
                              if (raw !== null && raw !== undefined && raw !== "") {
                                const num = Number(raw);
                                display = Number.isFinite(num)
                                  ? num.toFixed(1)
                                  : raw;
                              }

                              return (
                                <td key={c} className="border px-2 py-1 whitespace-nowrap">
                                  {display}
                                </td>
                              );
                            })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const fmt = (v, d = 2) => {
    const n = toNum(v);
    return n === null ? "—" : n.toFixed(d);
  };

  const pct = (curr, prev) => {
    const c = toNum(curr);
    const p = toNum(prev);
    if (c === null || p === null || p === 0) return null;
    return ((c - p) / p) * 100;
  };

  const renderPriceOiPcr = () => {

    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    if (!arr.length) {
      return <div className="text-sm text-red-500">No data available at the moment</div>;
    }

    const sortRows = (rows) => {
      const { key, direction } = sortConfig;
      const dir = direction === "asc" ? 1 : -1;

      return [...rows].sort((a, b) => {
        const av = a[key];
        const bv = b[key];

        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;

        return (av - bv) * dir;
      });
    };

    // Filter: show only rows where BOTH OI and PCR exist and not 0
    const rows = arr
      .filter((x) => {
        const oi = toNum(x.fut_oi);
        const pcr = toNum(x.op_pcr_oi);

        const oi20 = toNum(x.fut_oi_20_avg);
        const pcr20 = toNum(x.op_pcr_oi_20_avg);

        return (
          oi !== null && oi !== 0 &&
          pcr !== null && pcr !== 0 &&
          oi20 !== null && oi20 !== 0 &&
          pcr20 !== null && pcr20 !== 0
        );
      })
      .map((x) => {
        const currentPrice = x.currentPrice;
        const previousClose = x.previousClose;

        return {
          symbol: x.symbol,
          name: x.name,
          currentPrice,
          previousClose,
          chg : pct(currentPrice, previousClose),
          oi: x.fut_oi,
          fut_oi_chg_20_med: pct(x.fut_oi, x.fut_oi_20_avg),
          pcr: x.op_pcr_oi,
          op_pcr_chg_oi_20_avg: pct(x.op_pcr_oi, x.op_pcr_oi_20_avg)
        };
      })

    const sortedRows = sortRows(rows);

    if (!sortedRows.length) {
      return <div className="text-sm text-red-500">No data available at the moment</div>;
    }

    const rowCount = sortedRows.length;

    return (
      <div>
        <div className="text-sm text-gray-400">
          Total Symbols - {rowCount}
        </div>
        <div className="overflow-x-auto">
        <table className="table-auto w-full border-collapse text-sm text-gray-800">
          <colgroup>
            <col className="w-40" /> {/* Symbol auto */}
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-28" />
          </colgroup>
          <thead className="bg-gray-50 text-gray-600">
            <tr>
             <th className="text-left px-3 py-2 sticky left-0 z-30 bg-gray-50">
               Name
             </th>
              <th className="text-right px-3 py-2">Price</th>
              <SortableTh
                label="Price Chg"
                sortKey="chg"
                subLabel="(1D)"
              />
              <SortableTh
                label="Fut OI %"
                sortKey="oi"
              />
              <SortableTh
                label="Fut OI % Chg"
                sortKey="fut_oi_chg_20_med"
                subLabel="(Median - 20D)"
              />
              <SortableTh
                label="Options PCR"
                sortKey="pcr"
              />
              <SortableTh
                label="Options PCR Chg"
                sortKey="op_pcr_chg_oi_20_avg"
                subLabel="(Avg - 20D)"
              />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r, idx) => (
                <tr
                  key={r.symbol || idx}
                  className={`group ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50`}
                >
                  <td
                    className={`px-3 py-2 font-medium text-blue-600 whitespace-nowrap sticky left-0 z-20 border-r
                    ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    group-hover:bg-blue-50`}
                  >
                  <a href={`/symbol/${r.symbol}`} target="_blank" rel="noreferrer" className="hover:underline">
                    {r.name}
                  </a>
                </td>

                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {fmt(r.currentPrice, 2)}
                </td>

                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <ColorChange value={r.chg} />
                </td>

                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {fmt(r.oi, 2)}
                </td>

                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <ColorChange value={r.fut_oi_chg_20_med} />
                </td>

                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {fmt(r.pcr, 2)}
                </td>

                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <ColorChange value={r.op_pcr_chg_oi_20_avg} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  const renderPriceDeliveryATS = () => {
    const arr = Array.isArray(technicalsData)
      ? technicalsData
      : Object.values(technicalsData || {});

    if (!arr.length) {
      return <div className="text-sm text-red-500">No data available at the moment</div>;
    }

    // Reuse same sorting behavior as Price+OI+PCR
    const sortRows = (rows) => {
      const { key, direction } = sortConfig;
      const dir = direction === "asc" ? 1 : -1;

      return [...rows].sort((a, b) => {
        const av = a?.[key];
        const bv = b?.[key];

        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;

        // numeric compare
        return (av - bv) * dir;
      });
    };

    const rows = arr
      .filter((x) => {
        const deliveryPc = toNum(x.delivery_pc);
        const ats = toNum(x.ats);
        const vol = toNum(x.volume);

        // keep it strict-ish: if these missing, row is not meaningful
        return (
          vol !== null && vol !== 0 &&
          deliveryPc !== null && deliveryPc !== 0 &&
          ats !== null && ats !== 0
        );
      })
      .map((x) => {
        const price = x.currentPrice;
        const prev = x.previousClose;

        return {
          symbol: x.symbol,
          name: x.name,

          price,
          chg: pct(price, prev), // Price change (1D)

          volume: x.volume,
          volumeChg20: pct(x.volume, x.volume_20_avg), // vs 20D avg

          delivery: x.delivery,
          deliveryChg20: pct(x.delivery, x.delivery_20_avg),
          deliveryPc: x.delivery_pc,
          // Delivery% baseline is computed from totals: (20D Delivery total / 20D Volume total) * 100

          ats: x.ats,
          atsChg20: pct(x.ats, x.ats_20_avg),
        };
      });

    const sortedRows = sortRows(rows);

    if (!sortedRows.length) {
      return <div className="text-sm text-red-500">No data available at the moment</div>;
    }

    const rowCount = sortedRows.length;

    return (
      <div>
        <div className="text-sm text-gray-400">
          Total Symbols - {rowCount}
        </div>

        <div className="overflow-x-auto">
          <table className="table-auto w-full border-collapse text-sm text-gray-800">
            <colgroup>
              <col className="w-40" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
              <col className="w-28" />
            </colgroup>

            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 z-30 bg-gray-50">
                  Name
                </th>

                <th className="text-right px-3 py-2">Price</th>
                <SortableTh label="Price Chg" sortKey="chg" subLabel="(1D)" />

                <th className="text-right px-3 py-2">Volume</th>
                <SortableTh label="Vol Chg" sortKey="volumeChg20" subLabel="(Avg - 20D)" />

                <th className="text-right px-3 py-2">Del Vol</th>
                <SortableTh label="Del Vol Chg" sortKey="deliveryChg20" subLabel="(Avg - 20D)" />
                <SortableTh label="Delivery %" sortKey="deliveryPc" />

                <th className="text-right px-3 py-2">ATS</th>
                <SortableTh label="ATS Chg" sortKey="atsChg20" subLabel="(Avg - 20D)" />
              </tr>
            </thead>

            <tbody>
              {sortedRows.map((r, idx) => (
                <tr
                  key={r.symbol || idx}
                  className={`group ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50`}
                >
                  <td
                    className={`px-3 py-2 font-medium text-blue-600 whitespace-nowrap sticky left-0 z-20 border-r
                      ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} group-hover:bg-blue-50`}
                  >
                    <a
                      href={`/symbol/${r.symbol}`}
                      target="_blank"
                      rel="noreferrer"
                      className={`hover:underline ${r.name ? "text-blue-600" : "text-black"}`}
                    >
                      {r.name || r.symbol}
                    </a>
                  </td>

                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.price, 2)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.chg} />
                  </td>

                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.volume, 0)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.volumeChg20} />
                  </td>


                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.delivery, 0)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.deliveryChg20} />
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.deliveryPc, 2)}
                  </td>



                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {fmt(r.ats, 0)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <ColorChange value={r.atsChg20} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="p-4 text-sm text-gray-600">
        Please log in to view this content.
      </div>
    );
  }

  return (
    <div className="p-4">

      {/* Must Read */}
      <div className="mb-4 border border-yellow-300 bg-yellow-50 rounded p-3 text-xs text-gray-700">
        <div className="font-semibold text-gray-800 mb-1">
          ⚠️ Must Read
        </div>
        <p>
          These scanners are automatically generated using internal models and provided strictly for <b>educational and informational purposes only</b>.
          They do <b>not</b> constitute buy or sell recommendations or investment advice.
        </p>
      </div>

      <div className="mt-2 mb-2 flex items-center gap-1 text-gray-500 text-sm">
        <span>For deeper comparison, try:</span>
        <a
          href="/playground"
          className="text-blue-600 hover:underline font-medium"
        >
          Playground
        </a>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b mb-4">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setShowExplanation(false);
            }}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === tab
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Expand / Collapse button */}
      <button
        onClick={() => setShowExplanation((v) => !v)}
        className="flex items-center gap-1 text-xs border border-gray-300 rounded-full px-3 py-1 text-gray-700 hover:bg-gray-100 transition"
      >
        <span className="font-semibold">
          {showExplanation ? "−" : "+"}
        </span>
        <span>
          {showExplanation ? "Hide Explanation" : "Show Explanation"}
        </span>
      </button>

      {/* Content */}
      {showExplanation && (
        <div className="bg-gray-50 border rounded p-4 text-sm text-gray-700 leading-relaxed">
          <h2 className="font-semibold text-gray-800 mb-2">
            {activeTab} — Explanation
          </h2>
          {updateFreq[activeTab]}
          {tips[activeTab]}
        </div>
      )}


      <div className="mt-3">
        {activeTab === "Monthly Cycles" ? (
          renderMonthlyCycles()
        ) : activeTab === "Price + OI + PCR" ? (
          renderPriceOiPcr()
        ) : activeTab === "Price + Delivery + ATS" ? (
          renderPriceDeliveryATS()
        ) : (
          <div className="text-sm text-red-500">No data available at the moment</div>
        )}
      </div>
    </div>
  );
}

export default Scanners;
